import React, { useEffect, useState } from "react";
import Modal from "../Modal";
import TranscriptMessageBubble from "../TranscriptMessageBubble";
import { fetchAgentAlbertAlertNote } from "../../utils/api";
import {
  formatDateYmd,
  formatNoteTime,
  getVisibleObservationEntries,
  normalizeLocationText,
} from "../../utils/agentAlbertNoteFormat";

const AGENT_ROLE_RE = /\b(agent|ai|assistant|bot|kathy|albert)\b/i;
const CALLER_ROLE_RE = /\b(caller|user|human|provider|caregiver|client)\b/i;

const toRoleLabel = (rawRole = "") => {
  const role = String(rawRole || "").trim();
  if (!role) return "Caller";
  if (AGENT_ROLE_RE.test(role)) return "Kathy";
  if (CALLER_ROLE_RE.test(role)) return "Caller";
  return role.replace(/[_-]+/g, " ");
};

const parseTranscriptRows = (rawValue) => {
  const text = String(rawValue || "").trim();
  if (!text) return [];

  const lines = text
    .split(/\r?\n/)
    .map((line) => String(line || "").trim())
    .filter(Boolean);
  const rows = [];

  lines.forEach((line) => {
    const match = line.match(/^([A-Za-z][A-Za-z0-9_\s-]{0,40})\s*:\s*(.+)$/);
    if (match) {
      const rawRole = String(match[1] || "").trim();
      rows.push({
        role: toRoleLabel(rawRole),
        message: String(match[2] || "").trim(),
        isAgent: AGENT_ROLE_RE.test(rawRole),
      });
      return;
    }

    if (rows.length) {
      rows[rows.length - 1].message = `${rows[rows.length - 1].message} ${line}`.trim();
      return;
    }

    rows.push({
      role: "Transcript",
      message: line,
      isAgent: false,
    });
  });

  return rows.filter((row) => String(row.message || "").trim());
};

const displayShiftReportNoteTitle = (rawTitle, isArchived) => {
  const fallback = isArchived ? "Archived Shift Report Note" : "Shift Report Note";
  const title = String(rawTitle || "").trim();
  if (!title) return fallback;
  return title
    .replace(/Archived Agent Albert Notes?/gi, "Archived Shift Report Note")
    .replace(/Agent Albert Notes?/gi, "Shift Report Note");
};

const AgentAlbertNoteDetailModal = ({ alert, isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState(null);

  useEffect(() => {
    if (!isOpen || !alert?.clientId) {
      setNote(null);
      setError("");
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    setNote(null);

    fetchAgentAlbertAlertNote({
      clientId: alert.clientId,
      voiceCallId: alert.voiceCallId,
      visitNoteId: alert.visitNoteId,
    })
      .then((payload) => {
        if (cancelled) return;
        setNote(payload?.note || null);
        if (!payload?.note) setError("No shift report note found for this alert.");
      })
      .catch((err) => {
        if (cancelled) return;
        setNote(null);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load shift report note.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, alert?.clientId, alert?.voiceCallId, alert?.visitNoteId]);

  const title = displayShiftReportNoteTitle(
    note?.modalTitle,
    note?.isArchived,
  );

  const detailItems = note
    ? [
        { label: "Date", value: formatDateYmd(note.date) },
        { label: "Time", value: formatNoteTime(note) },
        { label: "Caller", value: note.caregiverName || "Albert AI" },
        {
          label: "Location",
          value: normalizeLocationText(note.location || note.observations?.agency),
        },
      ]
    : [];

  const observationEntries = note ? getVisibleObservationEntries(note) : [];
  const transcriptEntry = observationEntries.find(
    ([key]) => String(key || "").toLowerCase() === "transcript",
  );
  const observationRows = observationEntries.filter(
    ([key]) => String(key || "").toLowerCase() !== "transcript",
  );
  const transcriptRows = parseTranscriptRows(transcriptEntry?.[1]);
  const transcriptText = String(transcriptEntry?.[1] || "").trim();

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      {loading ? (
        <p style={{ margin: 0, color: "#666" }}>Loading shift report note...</p>
      ) : error ? (
        <p style={{ margin: 0, color: "#b91c1c" }}>{error}</p>
      ) : note ? (
        <div style={{ display: "grid", gap: 14 }}>
          <div
            style={{
              background: "#0f3d73",
              color: "#ffffff",
              borderRadius: 12,
              padding: "14px 16px",
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>
              Shift Report Note
            </div>
            <div style={{ marginTop: 4, fontSize: 13, opacity: 0.9 }}>
              Care summary details
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 10,
            }}
          >
            {detailItems.map((item) => (
              <div
                key={item.label}
                style={{
                  background: "#f8fbff",
                  border: "1px solid #dbeafe",
                  borderRadius: 12,
                  padding: "12px 14px",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: 12,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "#64748b",
                    fontWeight: 700,
                  }}
                >
                  {item.label}
                </p>
                <h3 style={{ margin: "6px 0 0 0", fontSize: 20, color: "#0f172a" }}>
                  {item.value || "--"}
                </h3>
              </div>
            ))}
          </div>

          <div
            style={{
              border: "1px solid #dbeafe",
              borderRadius: 12,
              overflow: "hidden",
              background: "#ffffff",
            }}
          >
            <div
              style={{
                padding: "12px 14px",
                borderBottom: "1px solid #dbeafe",
                background: "#f8fbff",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: 12,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "#64748b",
                  fontWeight: 700,
                }}
              >
                Agent Albert Summary
              </p>
              <h3 style={{ margin: "4px 0 0 0", fontSize: 18, color: "#0f172a" }}>
                Observations
              </h3>
            </div>

            <div style={{ padding: "12px 14px" }}>
              {observationRows.length === 0 ? (
                <p style={{ margin: 0, color: "#64748b" }}>No observations available.</p>
              ) : (
                <table
                  role="presentation"
                  width="100%"
                  cellPadding="0"
                  cellSpacing="0"
                  style={{ borderCollapse: "collapse" }}
                >
                  <tbody>
                    {observationRows.map(([key, value]) => (
                      <tr key={key}>
                        <td
                          style={{
                            width: "34%",
                            verticalAlign: "top",
                            padding: "10px 12px",
                            border: "1px solid #dbeafe",
                            background: "#f8fbff",
                            color: "#0f172a",
                            fontWeight: 700,
                          }}
                        >
                          {key.charAt(0).toUpperCase() + key.slice(1)}
                        </td>
                        <td
                          style={{
                            verticalAlign: "top",
                            padding: "10px 12px",
                            border: "1px solid #dbeafe",
                            color: "#1f2937",
                            whiteSpace: "pre-line",
                          }}
                        >
                          {String(value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div
            style={{
              border: "1px solid #dbeafe",
              borderRadius: 12,
              overflow: "hidden",
              background: "#ffffff",
            }}
          >
            <div
              style={{
                padding: "12px 14px",
                borderBottom: "1px solid #dbeafe",
                background: "#f8fbff",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: 12,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "#64748b",
                  fontWeight: 700,
                }}
              >
                Transcript
              </p>
              <h3 style={{ margin: "4px 0 0 0", fontSize: 18, color: "#0f172a" }}>
                Conversation Messages
              </h3>
            </div>
            <div style={{ padding: "12px 14px" }}>
              {transcriptRows.length ? (
                <div style={{ display: "grid", gap: 8 }}>
                  {transcriptRows.map((row, idx) => (
                    <TranscriptMessageBubble
                      key={`${row.role}-${idx}`}
                      role={row.role}
                      message={row.message}
                    />
                  ))}
                </div>
              ) : transcriptText ? (
                <pre
                  style={{
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    color: "#0f172a",
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    padding: "10px 12px",
                    background: "#f8fafc",
                    fontFamily: "inherit",
                  }}
                >
                  {transcriptText}
                </pre>
              ) : (
                <div style={{ color: "#64748b" }}>Transcript not available.</div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <p style={{ margin: 0, color: "#666" }}>No note details available.</p>
      )}
    </Modal>
  );
};

export default AgentAlbertNoteDetailModal;
